import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "./ui/sheet";
import {
  MobileSheet, MobileSheetBody, MobileSheetCloseButton, MobileSheetContent, MobileSheetFooter, MobileSheetHeader, MobileSheetTitle,
} from "./ui/mobile-sheet";
import useIsMobile from "../hooks/useIsMobile";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "./ui/responsive-popover";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "./ui/responsive-dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";
import {
  Building2, CalendarClock, CheckCircle2, ChevronDown, History, MoreHorizontal, Pencil, Trash2, UserRoundCog,
} from "lucide-react";
import {
  TASK_STATUS_LABELS, taskStatusBadgeClass, taskDisplayStatus,
  PRIORITY_LABELS, priorityBadgeClass, TASK_TYPE_LABELS, taskTypeBadgeClass, TASK_DOMAIN_LABELS,
  statusTimeline, lastEntryInto, elapsedSince,
} from "../lib/tasks";
import { getInitials, avatarColorClass } from "../lib/avatar";
import { companyDay } from "../lib/time";

// Shared by Tasks.jsx and ProspectDetail.jsx's Tâches tab: one task-detail
// surface instead of two. Formats a bare due_date ("YYYY-MM-DD", no time).
export function formatDueDate(dueDate, done) {
  if (!dueDate) return null;
  // Days are compared as calendar days in the company's zone, the same border as the server's « en retard » (lib/tasks.js::isTaskOverdue).
  const due = String(dueDate).slice(0, 10);
  const today = companyDay();
  const dayNumber = (day) => Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10)) / 86400000;
  if (!done && due <= today) {
    return { text: `En retard de ${Math.max(1, dayNumber(today) - dayNumber(due))} j`, overdue: true };
  }
  if (due === today) return { text: "Aujourd'hui", overdue: false };
  return { text: new Date(dueDate).toLocaleDateString("fr-FR"), overdue: false };
}

// Stacked (label above value) below sm: a 3-column grid in a 360px-wide sheet
// squeezes long values into a narrow column.
export function DetailRow({ label, children }) {
  return (
    <div className="flex flex-col gap-1 py-2 border-b border-slate-100 last:border-0 sm:grid sm:grid-cols-3 sm:gap-2 sm:items-start">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="sm:col-span-2 text-sm text-slate-900">{children}</div>
    </div>
  );
}

function PostponePopover({ task, onPostpone }) {
  const [value, setValue] = useState(task.due_date || "");
  return (
    <Popover onOpenChange={(open) => open && setValue(task.due_date || "")}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 shrink-0" data-testid="postpone-task">
          <CalendarClock size={14} /> Reporter
        </Button>
      </PopoverTrigger>
      <PopoverContent title="Reporter la tâche" className="w-64 space-y-3" align="start">
        <Input type="date" value={value} onChange={(e) => setValue(e.target.value)} data-testid="postpone-date" />
        <Button size="sm" className="w-full" onClick={() => onPostpone(task, value || null)}>
          Enregistrer
        </Button>
      </PopoverContent>
    </Popover>
  );
}

// Confirmation dialog for DELETE /tasks/{id}: the task goes to the trash (Ordre 28) and the person says why. `task` is the one pending
// deletion, or null when closed. `onConfirm(reason)`.
export function DeleteTaskDialog({ task, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (task) setReason("");
  }, [task]);
  const valid = reason.trim().length >= 3;
  return (
    <AlertDialog open={!!task} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer la tâche « {task?.title} » ?</AlertDialogTitle>
          <AlertDialogDescription>
            Elle va dans la corbeille : vous pourrez la restaurer pendant 30 jours, puis elle sera effacée.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <label htmlFor="delete-task-reason" className="text-sm font-medium text-slate-700">Motif</label>
          <Input id="delete-task-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} placeholder="Pourquoi la supprimer ?" data-testid="delete-task-reason" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { if (!valid) { e.preventDefault(); return; } onConfirm(reason.trim()); }}
            disabled={!valid} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-task"
          >
            Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const SYSTEM_EMAIL_SUFFIX = "@jamora-crm-system.internal";

// created_by holds a human's actor email or the bulk-import system tag
// (backend/scripts/import_tasks_2026.py's IMPORT_CREATED_BY), neither of which
// a business user should see as a raw address. GET /users/directory never
// exposes email, so only the viewer's own account resolves to a name; any
// other case falls back to a plain date rather than leaking the address.
function creationLabel(task, currentUser) {
  const email = task.created_by;
  const date = task.created_at ? new Date(task.created_at).toLocaleDateString("fr-FR") : null;
  if (email && currentUser?.email && email.toLowerCase() === currentUser.email.toLowerCase()) {
    return date ? `Créée par ${currentUser.name} · ${date}` : `Créée par ${currentUser.name}`;
  }
  if (email && email.toLowerCase().endsWith(SYSTEM_EMAIL_SUFFIX)) {
    return date ? `Créée automatiquement · ${date}` : "Créée automatiquement";
  }
  return date ? `Créée le ${date}` : "Créée";
}

// One responsable per line with a small avatar (mobile only): a hover-only
// name (AssigneeAvatars' tooltip) doesn't help a touch user. Desktop keeps
// the comma-joined directoryNames() line.
function AssigneeLine({ id, resolveAssignee, directoryNames }) {
  const person = resolveAssignee?.(id);
  const name = person?.name || directoryNames([id]) || "Utilisateur";
  return (
    <div className="flex items-center gap-2">
      <span
        className={`w-5 h-5 rounded-full flex items-center justify-center text-2xs font-semibold overflow-hidden shrink-0 ${
          person?.avatar_url ? "bg-slate-100" : avatarColorClass(id)
        }`}
      >
        {person?.avatar_url ? <img src={person.avatar_url} alt="" className="w-full h-full object-cover" /> : getInitials(name)}
      </span>
      <span className="truncate">{name}</span>
    </div>
  );
}

const formatDateTime = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

// Collapsible chronology at the very bottom of the detail: the creation, then every change of status with who, when and why. An older
// task (created before the trail existed) shows its creation only.
function StatusHistory({ task }) {
  const [open, setOpen] = useState(false);
  const changes = statusTimeline(task);
  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="task-status-history">
      <button
        type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 py-1 text-sm font-medium text-slate-700 min-h-[44px] md:min-h-0"
        data-testid="task-status-history-toggle"
      >
        <span className="inline-flex items-center gap-2"><History size={14} className="text-slate-500" /> Historique des statuts ({changes.length})</span>
        <ChevronDown size={16} className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="mt-2 space-y-3 border-l border-slate-200 pl-4" data-testid="task-status-history-list">
          {task.created_at && (
            <li className="relative text-sm">
              <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-slate-300" />
              <div className="text-slate-900">Tâche créée</div>
              <div className="text-xs text-slate-500">{formatDateTime(task.created_at)}</div>
            </li>
          )}
          {changes.map((entry, index) => (
            <li key={`${entry.at}-${index}`} className="relative text-sm" data-testid="task-status-history-entry">
              <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-jamora" />
              <div className="text-slate-900">
                {entry.from ? `${TASK_STATUS_LABELS[entry.from] || entry.from} → ` : ""}<span className="font-medium">{TASK_STATUS_LABELS[entry.status] || entry.status}</span>
              </div>
              <div className="text-xs text-slate-500">
                {formatDateTime(entry.at)}{entry.by_name ? ` · par ${entry.by_name}` : ""}
              </div>
              {entry.reason && <div className="mt-0.5 text-xs text-slate-600 whitespace-pre-wrap break-words">Motif : {entry.reason}</div>}
            </li>
          ))}
          {changes.length === 0 && <li className="text-xs text-slate-400">Aucun changement de statut enregistré depuis la création.</li>}
        </ol>
      )}
    </div>
  );
}

// `onEdit` is optional (no "Modifier" without it: the pilot workspace has no task form, whose prospect picker would
// query prospects the caller may not see). Without `onCycleStatus` the status badge stays static, so a read-only
// caller can still open the sheet. `resolveAssignee`/`currentUser` are optional too: AssigneeLine falls back to
// directoryNames, creationLabel to the unattributed date. Phone (below md): a bottom sheet; computer and tablet: the side drawer.
export function TaskDetailSheet({
  task, onOpenChange, canEdit, canDelete, directory, directoryNames, resolveAssignee, currentUser, navigate,
  onEdit, onDelete, onMarkDone, onPostpone, onAddAssignee, onCycleStatus,
}) {
  const mobile = useIsMobile();
  const due = task ? formatDueDate(task.due_date, task.done) : null;
  const display = task ? taskDisplayStatus(task) : null;
  // "Dernière modification" only when there really was one: a fresh task has
  // updated_at === created_at (tasks/service.py build_task_doc).
  const wasModified = task && task.updated_at && task.updated_at !== task.created_at;
  const hasSecondaryActions = task && (canDelete || (canEdit && directory.length > 0));
  const lastChange = task ? statusTimeline(task).slice(-1)[0] : null;
  const lastInProgress = task ? lastEntryInto(task, "en_cours") : null;

  const secondaryActionsItems = task && (
    <>
      {canEdit && directory.filter((u) => !(task.assigned_to || []).includes(u.id)).map((u) => (
        <DropdownMenuItem key={u.id} onClick={() => onAddAssignee(task, u.id)}>
          <UserRoundCog size={14} className="mr-2" /> Ajouter {u.name} comme responsable
        </DropdownMenuItem>
      ))}
      {canDelete && (
        <>
          {canEdit && directory.length > 0 && <DropdownMenuSeparator />}
          <DropdownMenuItem onClick={() => onDelete(task)} className="text-red-600 focus:text-red-600" data-testid="detail-delete-task">
            <Trash2 size={14} className="mr-2" /> Supprimer
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  const moreActions = (testId, buttonClass, button) => hasSecondaryActions && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{button(testId, buttonClass)}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">{secondaryActionsItems}</DropdownMenuContent>
    </DropdownMenu>
  );

  const body = task && (
    <>
      <div className="flex flex-wrap gap-2 mt-4">
        {canEdit && onCycleStatus ? (
          <button
            type="button" onClick={() => onCycleStatus(task)} data-testid="detail-cycle-status"
            title="Cliquer pour passer au statut suivant (À faire → En cours → Terminée)"
          >
            <Badge variant="outline" className={`border-transparent text-2xs font-medium ${taskStatusBadgeClass(display)}`}>
              {display === "en_retard" ? "En retard" : TASK_STATUS_LABELS[display]}
            </Badge>
          </button>
        ) : (
          <Badge variant="outline" className={`border-transparent text-2xs font-medium ${taskStatusBadgeClass(display)}`}>
            {display === "en_retard" ? "En retard" : TASK_STATUS_LABELS[display]}
          </Badge>
        )}
        <Badge variant="outline" className={`border-transparent text-2xs font-medium ${priorityBadgeClass(task.priority)}`}>
          {PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium}
        </Badge>
        <Badge variant="outline" className={`border-transparent text-2xs font-medium ${taskTypeBadgeClass(task.task_type)}`}>
          {TASK_TYPE_LABELS[task.task_type] || TASK_TYPE_LABELS.autre}
        </Badge>
      </div>

      <div className="mt-4">
        <DetailRow label="Prospect">
          {task.prospect_id ? (
            <button
              type="button"
              onClick={() => { onOpenChange(false); navigate(`/prospects/${task.prospect_id}`); }}
              className="inline-flex items-center gap-1.5 text-jamora hover:underline"
            >
              <Building2 size={14} /> Voir la fiche
            </button>
          ) : "Aucun prospect"}
        </DetailRow>
        <DetailRow label="Domaine">{TASK_DOMAIN_LABELS[task.domain] || "Non renseigné"}</DetailRow>
        <DetailRow label="Responsable(s)">
          {mobile ? (
            <div className="space-y-1.5">
              {(task.assigned_to || []).length === 0 ? (
                <span className="text-slate-400">Aucun responsable</span>
              ) : (
                task.assigned_to.map((id) => (
                  <AssigneeLine key={id} id={id} resolveAssignee={resolveAssignee} directoryNames={directoryNames} />
                ))
              )}
            </div>
          ) : (
            <span>{directoryNames(task.assigned_to) || "Aucun responsable"}</span>
          )}
        </DetailRow>
        <DetailRow label="Échéance">
          <div className="flex flex-wrap items-center gap-2">
            <span className={due?.overdue ? "text-red-600 font-medium" : ""}>{due ? due.text : "Aucune échéance"}</span>
            {canEdit && <PostponePopover task={task} onPostpone={onPostpone} />}
          </div>
        </DetailRow>
        {task.status === "en_cours" && lastInProgress && (
          <DetailRow label="En cours depuis">
            <span data-testid="task-in-progress-since">{formatDateTime(lastInProgress.at)} <span className="text-slate-500">({elapsedSince(lastInProgress.at)})</span></span>
          </DetailRow>
        )}
        {task.status !== "en_cours" && lastInProgress && (
          <DetailRow label="Dernière mise en cours">
            <span data-testid="task-last-in-progress">{formatDateTime(lastInProgress.at)}{lastInProgress.by_name ? ` · par ${lastInProgress.by_name}` : ""}</span>
          </DetailRow>
        )}
        {lastChange && (
          <DetailRow label="Dernier changement">
            <span data-testid="task-last-status-change">
              {TASK_STATUS_LABELS[lastChange.from] || lastChange.from} → {TASK_STATUS_LABELS[lastChange.status] || lastChange.status}, le {formatDateTime(lastChange.at)}{lastChange.by_name ? ` par ${lastChange.by_name}` : ""}
            </span>
          </DetailRow>
        )}
        {task.status === "termine" && task.completed_at && (
          <>
            {task.completed_performed_by && (
              <DetailRow label="Réalisée par"><span data-testid="task-completed-performed-by">{task.completed_performed_by.name || "Ancien membre de l'équipe"}</span></DetailRow>
            )}
            <DetailRow label="Clôture">{task.completed_performed_by ? "Saisie" : "Terminée"} par {task.completed_by_name || "un compte non enregistré"}, le {new Date(task.completed_at).toLocaleString("fr-FR")}</DetailRow>
            {task.completion_comment && <DetailRow label="Commentaire"><span className="whitespace-pre-wrap break-words">{task.completion_comment}</span></DetailRow>}
          </>
        )}
        {task.status === "annulee" && (
          <DetailRow label="Motif d'annulation">
            <span className="whitespace-pre-wrap break-words">{task.cancel_reason || "Aucun motif"}</span>
          </DetailRow>
        )}
        <DetailRow label="Description">
          {task.description ? <span className="whitespace-pre-wrap break-words">{task.description}</span> : <span className="text-slate-400">Aucune description</span>}
        </DetailRow>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
        {creationLabel(task, currentUser)}
        {wasModified && ` · Modifiée le ${new Date(task.updated_at).toLocaleDateString("fr-FR")}`}
      </div>

      <StatusHistory key={task.id} task={task} />
    </>
  );

  const actionButtons = task && (
    <div className="flex gap-2">
      {canEdit && onEdit && (
        <Button
          variant="outline" size="sm" className="flex-1 md:flex-none gap-1.5"
          onClick={() => onEdit(task)} data-testid="detail-edit-task"
        >
          <Pencil size={14} /> Modifier
        </Button>
      )}
      {canEdit && task.status !== "termine" && (
        <Button
          size="sm" className="flex-1 md:flex-none gap-1.5"
          onClick={() => onMarkDone(task)} data-testid="detail-mark-done"
        >
          <CheckCircle2 size={14} /> Marquer terminée
        </Button>
      )}
    </div>
  );

  const titleClass = task?.done ? "line-through text-slate-400" : "";

  if (mobile) {
    return (
      <MobileSheet open={!!task} onOpenChange={onOpenChange}>
        <MobileSheetContent data-testid="task-detail-sheet">
          {task && (
            <>
              <MobileSheetHeader>
                <MobileSheetTitle className={`break-words text-left ${titleClass}`}>{task.title}</MobileSheetTitle>
                <div className="flex items-center gap-1 shrink-0">
                  {moreActions("detail-more-actions-mobile", "h-11 w-11 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500", (testId, cls) => (
                    <button type="button" aria-label="Autres actions" className={cls} data-testid={testId}><MoreHorizontal size={18} /></button>
                  ))}
                  <MobileSheetCloseButton />
                </div>
              </MobileSheetHeader>
              <MobileSheetBody>{body}</MobileSheetBody>
              <MobileSheetFooter>{actionButtons}</MobileSheetFooter>
            </>
          )}
        </MobileSheetContent>
      </MobileSheet>
    );
  }

  return (
    <Sheet open={!!task} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full sm:max-w-md p-4 sm:p-6 overflow-hidden flex flex-col [&>button]:h-10 [&>button]:w-10 [&>button]:right-2 [&>button]:top-2 [&>button]:flex [&>button]:items-center [&>button]:justify-center"
        data-testid="task-detail-sheet"
      >
        {task && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <SheetHeader className="text-left">
                <div className="flex items-start justify-between gap-2 pr-6">
                  <SheetTitle className={`break-words ${titleClass}`}>{task.title}</SheetTitle>
                </div>
              </SheetHeader>
              {body}
            </div>

            <SheetFooter className="mt-6 shrink-0 sm:justify-between">
              {actionButtons}
              {moreActions("detail-more-actions", "h-9 w-9 p-0", (testId, cls) => (
                <Button variant="ghost" size="sm" className={cls} data-testid={testId} aria-label="Autres actions"><MoreHorizontal size={16} /></Button>
              ))}
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
